import { useState, useEffect, useRef, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import db from "../../config/firebase.config";
import { selectUser } from "../../features/userSlice";
import { useVerified } from "../../context/VerifiedContext";
import { UserNameWithBadge } from "../common/UserNameWithBadge";
import { VerificationBadge } from "../common/VerificationBadge";
import { uploadToCloudinary } from "../../utils/cloudinary";

dayjs.extend(relativeTime);

const USERNAME_REGEX = /^[a-zA-Z0-9_.]{3,20}$/;

const DEFAULT_AVATAR =
    "https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_960_720.png";

export const ProfilePage = () => {
    const user = useSelector(selectUser);
    const { isVerified, getBadgeType, verifiedMap } = useVerified();
    const [profile, setProfile] = useState(null);
    const [posts, setPosts] = useState([]);
    const [editOpen, setEditOpen] = useState(false);

    useEffect(() => {
        if (!user?.uid) return;
        const unsub = db.collection("users").doc(user.uid).onSnapshot(
            (doc) => setProfile(doc.exists ? doc.data() : null),
            () => setProfile(null)
        );
        return unsub;
    }, [user?.uid]);

    useEffect(() => {
        if (!user?.uid) return;
        const unsub = db
            .collection("questions")
            .where("q_user_id", "==", user.uid)
            .onSnapshot((snap) => {
                const list = [];
                snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
                const getTs = (p) => p.created_at || p.createdAt || 0;
                list.sort((a, b) => getTs(b) - getTs(a));
                setPosts(list);
            });
        return unsub;
    }, [user?.uid]);

    const badgeType = getBadgeType(user?.uid, user?.email);
    const verified = isVerified(user?.uid, user?.email);
    const name = profile?.displayName || user?.displayName || "Fellora Fellow";
    const username = profile?.username || (user?.email ? user.email.split("@")[0] : "");
    const photoURL = profile?.photoURL || user?.photoURL || DEFAULT_AVATAR;
    const bio = profile?.bio || "";
    const isOwn = true;

    return (
        <Page>
            <HeaderCard>
                <AvatarWrap>
                    <Avatar
                        src={photoURL}
                        alt={name}
                        referrerPolicy="no-referrer"
                    />
                    {verified && (
                        <AvatarBadge>
                            <VerificationBadge type={badgeType || "blue"} size="md" />
                        </AvatarBadge>
                    )}
                </AvatarWrap>

                <Identity>
                    <NameRow>
                        <UserNameWithBadge
                            name={name}
                            uid={user?.uid}
                            email={user?.email}
                            badgeSize="md"
                            className="profile-name"
                        />
                    </NameRow>
                    <Handle>@{username}</Handle>
                    {bio && <Bio>"{bio}"</Bio>}
                    {isOwn && (
                        <EditBtn onClick={() => setEditOpen(true)}>
                            <EditIcon />
                            Edit Profile
                        </EditBtn>
                    )}
                </Identity>
            </HeaderCard>

            <Section>
                <SectionHeader>
                    <h2>Recent Posts</h2>
                    <Link to="/home" className="create-link">
                        + Create Post
                    </Link>
                </SectionHeader>

                {posts.length === 0 ? (
                    <EmptyState>
                        <p>You haven't posted anything yet.</p>
                        <Link to="/home">+ Create Post</Link>
                    </EmptyState>
                ) : (
                    <PostList>
                        {posts.map((p) => (
                            <PostCard key={p.id} to={`/question/${p.id}`}>
                                <div className="top">
                                    <span className="community">
                                        {p.tags?.[0] ? `#${p.tags[0]}` : "General"}
                                    </span>
                                    <span className="time">
                                        {dayjs(p.created_at || p.createdAt).fromNow()}
                                    </span>
                                </div>
                                <h3>{p.question}</h3>
                                <div className="stats">
                                    <span>▲ {p.up_votes || 0} likes</span>
                                    <span>💬 {p.commentCount ?? p.answer_count ?? 0} comments</span>
                                </div>
                            </PostCard>
                        ))}
                    </PostList>
                )}
            </Section>

            {editOpen && (
                <EditProfileModal
                    user={user}
                    profile={profile}
                    onClose={() => setEditOpen(false)}
                />
            )}
        </Page>
    );
};

/* ─── Edit Profile Modal ──────────────────────────────────────────────────── */

const EditProfileModal = ({ user, profile, onClose }) => {
    const [fullName, setFullName] = useState(profile?.displayName || user?.displayName || "");
    const [username, setUsername] = useState(profile?.username || "");
    const [bio, setBio] = useState(profile?.bio || "");
    const [avatar, setAvatar] = useState(profile?.photoURL || user?.photoURL || "");
    const [cropOpen, setCropOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState("");
    const [dragOver, setDragOver] = useState(false);
    const [usernameStatus, setUsernameStatus] = useState(null); // null | checking | available | taken
    const fileInputRef = useRef(null);

    const checkUsername = useCallback(
        async (value) => {
            const val = (value || "").trim();
            if (!val) {
                setUsernameStatus(null);
                return;
            }
            if (!USERNAME_REGEX.test(val)) {
                setUsernameStatus("invalid");
                return;
            }
            setUsernameStatus("checking");
            try {
                const snap = await db.collection("users").get();
                let taken = false;
                snap.forEach((d) => {
                    if (d.id !== user?.uid && (d.data().username || "").toLowerCase() === val.toLowerCase()) {
                        taken = true;
                    }
                });
                setUsernameStatus(taken ? "taken" : "available");
            } catch {
                setUsernameStatus(null);
            }
        },
        [user?.uid]
    );

    useEffect(() => {
        const t = setTimeout(() => checkUsername(username), 400);
        return () => clearTimeout(t);
    }, [username, checkUsername]);

    const handleFile = (file) => {
        if (!file || !file.type.startsWith("image/")) return;
        setAvatar(URL.createObjectURL(file));
        setCropOpen(true);
        setSaved(false);
    };

    const handleCropApply = (blob) => {
        if (blob) {
            setAvatar(URL.createObjectURL(blob));
        }
        setCropOpen(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFile(e.dataTransfer.files?.[0]);
    };

    const handleSave = async () => {
        setError("");
        const nameVal = fullName.trim();
        const userVal = username.trim();
        if (!nameVal) return setError("Full name is required.");
        if (!USERNAME_REGEX.test(userVal)) {
            return setError("Username must be 3–20 characters using letters, numbers, _ and . only.");
        }
        if (usernameStatus === "taken") {
            return setError("That username is already taken.");
        }

        setSaving(true);
        try {
            let photoURL = avatar;
            if (avatar && avatar.startsWith("blob:")) {
                const blob = await (await fetch(avatar)).blob();
                const res = await uploadToCloudinary(blob, "avatar");
                photoURL = res.url;
            }
            await db.collection("users").doc(user.uid).set(
                {
                    displayName: nameVal,
                    username: userVal,
                    bio: bio.trim(),
                    photoURL,
                },
                { merge: true }
            );
            setSaved(true);
            setTimeout(() => onClose(), 1400);
        } catch {
            setError("Save failed. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <h2>Edit Profile</h2>
                    <CloseBtn onClick={onClose} aria-label="Close">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                        </svg>
                    </CloseBtn>
                </ModalHeader>

                {saved ? (
                    <SuccessMsg>✅ Profile updated successfully.</SuccessMsg>
                ) : (
                    <>
                        <AvatarEditor>
                            <AvatarPreview src={avatar || DEFAULT_AVATAR} alt="avatar preview" />
                            <div className="actions">
                                <button
                                    className="upload"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    {avatar ? "Replace Picture" : "Upload from device"}
                                </button>
                                {avatar && (
                                    <button className="remove" onClick={() => setAvatar("")}>
                                        Remove
                                    </button>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={(e) => handleFile(e.target.files?.[0])}
                                />
                            </div>
                        </AvatarEditor>

                        {cropOpen && (
                            <CropBox
                                imageUrl={avatar}
                                onCancel={() => {
                                    setCropOpen(false);
                                }}
                                onApply={handleCropApply}
                            />
                        )}

                        <DragZone
                            $dragOver={dragOver}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragOver(true);
                            }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}
                        >
                            or drag &amp; drop an image here
                        </DragZone>

                        <Field>
                            <label>Full name</label>
                            <input
                                type="text"
                                value={fullName}
                                onChange={(e) => {
                                    setFullName(e.target.value);
                                    setSaved(false);
                                }}
                                placeholder="Your full name"
                            />
                        </Field>

                        <Field>
                            <label>Username</label>
                            <div className="username-row">
                                <span className="at">@</span>
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => {
                                        setUsername(e.target.value);
                                        setSaved(false);
                                    }}
                                    placeholder="username"
                                />
                            </div>
                            <UsernameStatus $status={usernameStatus}>
                                {usernameStatus === "checking" && "Checking availability…"}
                                {usernameStatus === "available" && "✅ Username available"}
                                {usernameStatus === "taken" && "❌ Username already taken"}
                                {usernameStatus === "invalid" &&
                                    "3–20 characters: letters, numbers, _ and . only"}
                            </UsernameStatus>
                        </Field>

                        <Field>
                            <label>Bio</label>
                            <textarea
                                rows={3}
                                value={bio}
                                onChange={(e) => {
                                    setBio(e.target.value);
                                    setSaved(false);
                                }}
                                placeholder="Tell fellows a little about yourself (optional)"
                            />
                        </Field>

                        {error && <ErrorMsg>{error}</ErrorMsg>}

                        <ModalFooter>
                            <CancelBtn onClick={onClose}>Cancel</CancelBtn>
                            <SaveBtn onClick={handleSave} disabled={saving || usernameStatus === "checking"}>
                                {saving ? <Spinner /> : "Save Changes"}
                            </SaveBtn>
                        </ModalFooter>
                    </>
                )}
            </Modal>
        </Overlay>
    );
};

/* ─── Crop Box (square, zoom + pan, canvas-cropped on mount) ──────────────── */
const CropBox = ({ imageUrl, onCancel, onApply }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const imgRef = useRef(null);

    useEffect(() => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            imgRef.current = img;
            draw();
        };
        img.src = imageUrl;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageUrl]);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        const img = imgRef.current;
        if (!canvas || !img) return;
        const size = 220;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, size, size);
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, size, size);
        const scale = (size * zoom) / Math.min(img.width, img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        const cx = size / 2 + offset.x * size * 0.35;
        const cy = size / 2 + offset.y * size * 0.35;
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(img, cx - w / 2, cy - h / 2, w, h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zoom, offset]);

    useEffect(() => {
        draw();
    }, [draw, imageUrl]);

    const handlePointer = (e) => {
        const rect = containerRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
        const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
        setOffset({ x, y });
    };

    const cropToBlob = () => {
        return new Promise((resolve) => {
            canvasRef.current?.toBlob(resolve, "image/jpeg", 0.9);
        });
    };

    return (
        <CropWrap>
            <div
                className="crop-preview"
                ref={containerRef}
                onPointerMove={handlePointer}
                onPointerDown={handlePointer}
            >
                <canvas ref={canvasRef} />
            </div>
            <input
                type="range"
                min="1"
                max="3"
                step="0.05"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
            />
            <div className="crop-hint">Drag to position · zoom to fit</div>
            <div className="crop-btns">
                <button onClick={onCancel}>Cancel</button>
                <button
                    className="primary"
                    onClick={async () => {
                        const blob = await cropToBlob();
                        onApply(blob);
                    }}
                >
                    Apply Crop
                </button>
            </div>
        </CropWrap>
    );
};

/* ─── Styles ──────────────────────────────────────────────────────────────── */

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`
    from { transform: translateY(30px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;

const Page = styled.div`
    max-width: 760px;
    margin: 0 auto;
    padding: 24px 16px 60px;
    background-color: #faf7f2;
    min-height: 100vh;
`;

const HeaderCard = styled.div`
    display: flex;
    align-items: center;
    gap: 22px;
    background: #ffffff;
    border: 1px solid rgba(201, 160, 99, 0.25);
    border-radius: 18px;
    padding: 26px 28px;
    box-shadow: 0 6px 24px rgba(10, 20, 40, 0.06);
    animation: ${fadeIn} 0.3s ease;

    @media (max-width: 520px) {
        flex-direction: column;
        text-align: center;
    }
`;

const AvatarWrap = styled.div`
    position: relative;
    flex-shrink: 0;
`;

const Avatar = styled.img`
    width: 120px;
    height: 120px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid #ffffff;
    box-shadow: 0 0 0 2px rgba(29, 161, 242, 0.25), 0 8px 20px rgba(10, 20, 40, 0.15);
`;

const AvatarBadge = styled.div`
    position: absolute;
    bottom: 2px;
    right: -2px;
    background: #ffffff;
    border-radius: 50%;
    padding: 1px;
`;

const Identity = styled.div`
    flex: 1;
    min-width: 0;

    .profile-name {
        font-size: 30px;
        font-weight: 800;
        color: #0a1428;
        display: inline-flex;
    }
`;

const NameRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Handle = styled.p`
    font-size: 20px;
    font-weight: 600;
    color: #636466;
    margin: 4px 0 8px;
`;

const Bio = styled.p`
    font-size: 18px;
    color: #555;
    font-style: italic;
    line-height: 1.45;
    margin: 0 0 14px;
`;

const EditBtn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 9px 22px;
    border-radius: 30px;
    border: 1.5px solid #1DA1F2;
    background: #ffffff;
    color: #1DA1F2;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #e8f5fe;
    }
`;

const EditIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.996.996 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
    </svg>
);

const Section = styled.div`
    margin-top: 26px;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;

    h2 {
        font-size: 24px;
        font-weight: 800;
        color: #0a1428;
        margin: 0;
    }

    .create-link {
        font-size: 17px;
        font-weight: 700;
        color: #1DA1F2;
        text-decoration: none;

        &:hover { text-decoration: underline; }
    }
`;

const EmptyState = styled.div`
    background: #ffffff;
    border: 1px dashed rgba(201, 160, 99, 0.45);
    border-radius: 14px;
    padding: 48px 20px;
    text-align: center;
    color: #888;

    p {
        font-size: 19px;
        margin: 0 0 16px;
    }

    a {
        display: inline-block;
        padding: 10px 28px;
        border-radius: 30px;
        background: linear-gradient(135deg, #1DA1F2, #0d8ecf);
        color: #fff;
        font-size: 18px;
        font-weight: 700;
        text-decoration: none;

        &:hover { opacity: 0.88; }
    }
`;

const PostList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const PostCard = styled(Link)`
    display: block;
    background: #ffffff;
    border: 1px solid rgba(201, 160, 99, 0.25);
    border-radius: 12px;
    padding: 16px 20px;
    text-decoration: none;
    color: inherit;
    box-shadow: 0 3px 12px rgba(10, 20, 40, 0.04);
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(10, 20, 40, 0.08);
    }

    .top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 6px;

        .community {
            font-size: 16px;
            font-weight: 700;
            color: #1DA1F2;
        }

        .time {
            font-size: 15px;
            color: #999;
        }
    }

    h3 {
        font-size: 20px;
        font-weight: 700;
        color: #0a1428;
        line-height: 1.4;
        margin: 0 0 10px;
    }

    .stats {
        display: flex;
        gap: 18px;
        font-size: 16px;
        color: #636466;
    }
`;

/* ─── Modal ───────────────────────────────────────────────────────────────── */
const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(10, 20, 40, 0.55);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: ${fadeIn} 0.2s ease;
`;

const Modal = styled.div`
    background: #ffffff;
    border-radius: 18px;
    width: 100%;
    max-width: 480px;
    padding: 26px 28px 24px;
    position: relative;
    max-height: 92vh;
    overflow-y: auto;
    animation: ${slideUp} 0.25s ease;
    box-shadow: 0 20px 60px rgba(10, 20, 40, 0.18);
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;

    h2 {
        font-size: 24px;
        font-weight: 800;
        color: #0a1428;
        margin: 0;
    }
`;

const CloseBtn = styled.button`
    background: #f5f5f5;
    border: none;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #555;

    &:hover { background: #e0e0e0; color: #000; }
`;

const AvatarEditor = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 12px;

    .actions {
        display: flex;
        flex-direction: column;
        gap: 8px;

        button {
            border-radius: 20px;
            padding: 7px 16px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            border: none;
            transition: opacity 0.15s;

            &.upload {
                background: #e8f5fe;
                color: #1DA1F2;
            }

            &.remove {
                background: #ffebee;
                color: #e53935;
            }

            &:hover { opacity: 0.85; }
        }
    }
`;

const AvatarPreview = styled.img`
    width: 72px;
    height: 72px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #e0dbd1;
    flex-shrink: 0;
`;

const DragZone = styled.div`
    border: 1.5px dashed ${(p) => (p.$dragOver ? "#1DA1F2" : "rgba(201,160,99,0.5)")};
    background: ${(p) => (p.$dragOver ? "#e8f5fe" : "#faf7f2")};
    border-radius: 10px;
    padding: 12px;
    text-align: center;
    font-size: 15px;
    color: #888;
    margin-bottom: 16px;
    transition: all 0.2s;
`;

const Field = styled.div`
    margin-bottom: 16px;

    label {
        display: block;
        font-size: 17px;
        font-weight: 700;
        color: #0a1428;
        margin-bottom: 6px;
    }

    input, textarea {
        width: 100%;
        padding: 10px 14px;
        border: 1.5px solid rgba(201, 160, 99, 0.4);
        border-radius: 8px;
        font-size: 17px;
        font-family: inherit;
        box-sizing: border-box;
        outline: none;
        transition: border-color 0.2s;
        background: #faf7f2;

        &:focus {
            border-color: #1DA1F2;
            background: #fff;
        }
    }

    textarea { resize: vertical; }

    .username-row {
        display: flex;
        align-items: center;

        .at {
            font-size: 20px;
            font-weight: 700;
            color: #636466;
            margin-right: 6px;
        }
    }
`;

const UsernameStatus = styled.div`
    margin-top: 5px;
    font-size: 15px;
    font-weight: 600;
    color: ${(p) =>
        p.$status === "taken" || p.$status === "invalid"
            ? "#e53935"
            : p.$status === "available"
            ? "#22c55e"
            : "#888"};
`;

const ErrorMsg = styled.div`
    font-size: 16px;
    color: #e53935;
    font-weight: 600;
    margin-bottom: 10px;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 6px;
`;

const CancelBtn = styled.button`
    padding: 10px 24px;
    border-radius: 30px;
    border: 1.5px solid #d0d0d0;
    background: #fff;
    color: #555;
    font-size: 17px;
    font-weight: 600;
    cursor: pointer;

    &:hover { background: #f5f5f5; }
`;

const SaveBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: 130px;
    padding: 10px 24px;
    border-radius: 30px;
    border: none;
    background: linear-gradient(135deg, #1DA1F2, #0d8ecf);
    color: #fff;
    font-size: 17px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover { opacity: 0.88; }
    &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const SuccessMsg = styled.div`
    text-align: center;
    padding: 40px 0;
    font-size: 22px;
    font-weight: 700;
    color: #22c55e;
`;

const Spinner = styled.div`
    width: 18px;
    height: 18px;
    border: 2.5px solid rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;

/* ─── Crop styles ─────────────────────────────────────────────────────────── */
const CropWrap = styled.div`
    background: #0a1428;
    border-radius: 12px;
    padding: 14px;
    margin-bottom: 16px;

    .crop-preview {
        width: 220px;
        height: 220px;
        margin: 0 auto;
        border-radius: 50%;
        overflow: hidden;
        cursor: grab;
        touch-action: none;
        background: #1a2438;

        canvas {
            width: 100%;
            height: 100%;
            display: block;
        }
    }

    input[type="range"] {
        display: block;
        width: 100%;
        margin: 12px auto 6px;
        accent-color: #1DA1F2;
    }

    .crop-hint {
        text-align: center;
        color: #9aa6bd;
        font-size: 14px;
        margin-bottom: 10px;
    }

    .crop-btns {
        display: flex;
        justify-content: flex-end;
        gap: 8px;

        button {
            border: none;
            border-radius: 20px;
            padding: 6px 16px;
            font-size: 15px;
            font-weight: 600;
            cursor: pointer;

            &:first-child {
                background: #2a3550;
                color: #cfd6e6;
            }

            &.primary {
                background: #1DA1F2;
                color: #fff;
            }
        }
    }
`;

export default ProfilePage;
